import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { FAQList } from '@/components/knowledge/faq-list'
import { FAQEditor } from '@/components/knowledge/faq-editor'
import { FAQDetail } from '@/components/knowledge/faq-detail'
import { PageWrapper } from '@/components/animated/page-wrapper'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'

type ViewMode = 'list' | 'detail' | 'edit' | 'create'

export function KnowledgeManagement() {
  const [viewMode, setViewMode] = useState<ViewMode>('list')
  const [selectedFAQ, setSelectedFAQ] = useState<any>(null)
  const [showDialog, setShowDialog] = useState(false)
  const [searchParams, setSearchParams] = useSearchParams()
  const [allFAQs, setAllFAQs] = useState<any[]>([])
  const [currentIndex, setCurrentIndex] = useState(-1)

  // Extract filter params from URL
  const urlFilters = {
    source: searchParams.get('source') || undefined,
    tag: searchParams.get('tag') || undefined
  }

  // Handle URL parameters for external navigation
  useEffect(() => {
    const view = searchParams.get('view')
    const id = searchParams.get('id')
    const context = searchParams.get('context')
    const filterType = searchParams.get('filterType')
    const filterValue = searchParams.get('filterValue')

    if (view && id) {
      console.log('URL params:', { view, id, context, filterType, filterValue })

      if (context === 'filtered' && filterType && filterValue) {
        console.log('Loading filtered FAQ with navigation context')
        loadFilteredFAQs(id, view as ViewMode, filterType, filterValue)
      } else {
        console.log('Loading single FAQ')
        loadFAQById(id, view as ViewMode)
      }
    }
  }, [searchParams])

  const loadFilteredFAQs = async (selectedId: string, targetView: ViewMode, filterType: string, filterValue: string) => {
    try {
      console.log('Loading filtered FAQs for navigation:', { filterType, filterValue, selectedId })

      let query = supabase.from('knowledge').select('*')

      if (filterType === 'tag') {
        const decodedTag = decodeURIComponent(filterValue)
        query = query.contains('tags', [decodedTag])
      } else if (filterType === 'source') {
        // Skip source filtering for now to avoid TypeScript issues
        console.log('Source filtering temporarily disabled')
      }

      const { data, error } = await query
        .order('created_at', { ascending: false })
        .limit(100)

      if (error) throw error

      const faqs = data || []
      console.log(`Loaded ${faqs.length} filtered FAQs for navigation`)

      // Find the selected FAQ and set navigation context
      const selectedFAQ = faqs.find(f => f.id === selectedId)
      if (selectedFAQ) {
        setAllFAQs(faqs)
        const index = faqs.findIndex(f => f.id === selectedId)
        setCurrentIndex(index)
        setSelectedFAQ(selectedFAQ)

        if (targetView === 'edit') {
          setViewMode('edit')
          setShowDialog(true)
        } else {
          setViewMode('detail')
        }
      }

      // Clear URL params after loading
      setSearchParams({})
    } catch (error) {
      console.error('Error loading filtered FAQs:', error)
      setViewMode('list')
      setSearchParams({})
    }
  }

  const loadFAQById = async (id: string, targetView: ViewMode) => {
    try {
      const { data, error } = await supabase
        .from('knowledge')
        .select('*')
        .eq('id', id)
        .single()

      if (error) throw error

      setSelectedFAQ(data)
      if (targetView === 'edit') {
        setViewMode('edit')
        setShowDialog(true)
      } else {
        setViewMode('detail')
      }

      // Clear URL params after loading
      setSearchParams({})
    } catch (error) {
      console.error('Error loading FAQ:', error)
      // Fallback to list view if FAQ not found
      setViewMode('list')
      setSearchParams({})
    }
  }

  const handleSelectFAQ = (faq: any, faqs?: any[]) => {
    setSelectedFAQ(faq)
    setViewMode('detail')

    // Update navigation context
    if (faqs) {
      setAllFAQs(faqs)
      const index = faqs.findIndex(f => f.id === faq.id)
      setCurrentIndex(index)
    }
  }

  const navigateToNext = () => {
    if (currentIndex < allFAQs.length - 1) {
      const nextFAQ = allFAQs[currentIndex + 1]
      setSelectedFAQ(nextFAQ)
      setCurrentIndex(currentIndex + 1)
    }
  }

  const navigateToPrevious = () => {
    if (currentIndex > 0) {
      const previousFAQ = allFAQs[currentIndex - 1]
      setSelectedFAQ(previousFAQ)
      setCurrentIndex(currentIndex - 1)
    }
  }

  const navigateToRandom = () => {
    if (allFAQs.length > 0) {
      const randomIndex = Math.floor(Math.random() * allFAQs.length)
      const randomFAQ = allFAQs[randomIndex]
      setSelectedFAQ(randomFAQ)
      setCurrentIndex(randomIndex)
    }
  }

  const handleCreateNew = () => {
    setSelectedFAQ(null)
    setViewMode('create')
    setShowDialog(true)
  }

  const handleEdit = () => {
    setViewMode('edit')
    setShowDialog(true)
  }

  const handleEditFAQ = (faq: any) => {
    setSelectedFAQ(faq)
    setViewMode('edit')
    setShowDialog(true)
  }

  const handleSave = (faq: any) => {
    setShowDialog(false)
    setViewMode('list')
    setSelectedFAQ(null)
  }

  const handleCancel = () => {
    setShowDialog(false)
    if (viewMode === 'create') {
      setViewMode('list')
      setSelectedFAQ(null)
    } else {
      setViewMode('detail')
    }
  }

  // Handle dialog open/close more reliably
  const handleDialogOpenChange = (open: boolean) => {
    if (!open) {
      // Dialog is closing
      handleCancel()
    } else {
      // Dialog is opening
      setShowDialog(true)
    }
  }

  const handleCloseDetail = () => {
    setViewMode('list')
    setSelectedFAQ(null)
  }

  return (
    <PageWrapper>
      <div className="space-y-2">
        {/* Main Content */}
        {viewMode === 'list' && (
          <FAQList
            onSelectFAQ={handleSelectFAQ}
            onEditFAQ={handleEditFAQ}
            onCreateNew={handleCreateNew}
            filter={urlFilters}
          />
        )}

        {viewMode === 'detail' && selectedFAQ && (
          <div className="space-y-2">
            {/* Escalation Type Badge */}
            {selectedFAQ?.escalation_rule && (
              <div className="flex justify-start">
                <Badge
                  variant={
                    selectedFAQ.escalation_rule === 'self_solve' ? 'default' :
                    selectedFAQ.escalation_rule === 'mission_solar' ? 'secondary' :
                    'destructive'
                  }
                  className="font-medium"
                >
                  {selectedFAQ.escalation_rule === 'self_solve' ? 'Self Solve' :
                   selectedFAQ.escalation_rule === 'mission_solar' ? 'Mission Solar' :
                   'Neoom'}
                </Badge>
              </div>
            )}
            <FAQDetail
              faq={selectedFAQ}
              onEdit={handleEdit}
              onClose={handleCloseDetail}
              onNavigateNext={navigateToNext}
              onNavigatePrevious={navigateToPrevious}
              onNavigateRandom={navigateToRandom}
              hasNext={currentIndex < allFAQs.length - 1}
              hasPrevious={currentIndex > 0}
            />
          </div>
        )}

        {/* Edit/Create Dialog */}
        <Dialog open={showDialog} onOpenChange={handleDialogOpenChange}>
          <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {viewMode === 'create' ? 'Neue FAQ erstellen' : 'FAQ bearbeiten'}
              </DialogTitle>
              <DialogDescription>
                {viewMode === 'create'
                  ? 'Erstellen Sie eine neue FAQ für die Wissensdatenbank.'
                  : 'Bearbeiten Sie die vorhandene FAQ und speichern Sie Ihre Änderungen.'
                }
              </DialogDescription>
            </DialogHeader>
            <FAQEditor
              faq={viewMode === 'edit' ? selectedFAQ : null}
              onSave={handleSave}
              onCancel={handleCancel}
            />
          </DialogContent>
        </Dialog>
      </div>
    </PageWrapper>
  )
}